import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AppHeader } from "@/components/app/AppHeader";
import { SubmissionsTable } from "@/components/submissions/SubmissionsTable";
import { SubmissionModal } from "@/components/submissions/SubmissionModal";
import { TaskUpdateModal } from "@/components/editor/TaskUpdateModal";
import { VideoSubmissionsTable } from "@/components/editor/VideoSubmissionsTable";
import { OtherTasksTable } from "@/components/common/OtherTasksTable";
import { DesignerTaskUpdateModal } from "@/components/designer/DesignerTaskUpdateModal";
import { AiTaskUpdateModal } from "@/components/ai/AiTaskUpdateModal";
import { AiBulkAddModal } from "@/components/ai/AiBulkAddModal";
import { AiSubmissionsTable } from "@/components/ai/AiSubmissionsTable";
import { DesignerBulkAddModal } from "@/components/designer/DesignerBulkAddModal";
import { DesignerSubmissionsTable } from "@/components/designer/DesignerSubmissionsTable";
import { ScriptwriterTaskUpdateModal } from "@/components/scriptwriter/ScriptwriterTaskUpdateModal";
import { ScriptwriterBulkAddModal } from "@/components/scriptwriter/ScriptwriterBulkAddModal";
import { ScriptwriterSubmissionsTable } from "@/components/scriptwriter/ScriptwriterSubmissionsTable";
import { MarketerTaskUpdateModal } from "@/components/marketer/MarketerTaskUpdateModal";
import { MarketerTasksTable } from "@/components/marketer/MarketerTasksTable";
import { SeoTaskUpdateModal } from "@/components/seo/SeoTaskUpdateModal";
import { SeoTasksTable } from "@/components/seo/SeoTasksTable";
import { SmmTaskUpdateModal } from "@/components/smm/SmmTaskUpdateModal";
import { SmmTasksTable } from "@/components/smm/SmmTasksTable";
import { useMySmmOtherTasks, useMySmmTasks } from "@/hooks/use-smm-tasks";
import { AdminTaskUpdateModal } from "@/components/admin/AdminTaskUpdateModal";
import { AdminTasksTable } from "@/components/admin/AdminTasksTable";
import { useMyAdminOtherTasks, useMyAdminTasks } from "@/hooks/use-admin-tasks";
import type { AdminOtherTask, AdminTask } from "@/lib/admin-tasks";
import { CallingTaskUpdateModal } from "@/components/calling/CallingTaskUpdateModal";
import { CallingSubmissionsTable } from "@/components/calling/CallingSubmissionsTable";
import { useMyCallingOtherTasks, useMyCallingSubmissions } from "@/hooks/use-calling-tasks";
import type { CallingOtherTask, CallingSubmission } from "@/lib/calling-tasks";
import type { SmmOtherTask, SmmTask } from "@/lib/smm-tasks";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DateRangeFilter,
  EMPTY_RANGE,
  filterByDateField,
  type DateRange,
} from "@/components/common/DateRangeFilter";
import { AdsDateRangePicker } from "@/components/common/AdsDateRangePicker";
import { TimelineView } from "@/components/common/TimelineView";
import { ViewModeToggle, type ViewMode } from "@/components/common/ViewModeToggle";
import {
  adminOtherToTimeline,
  adminTaskToTimeline,
  callingOtherToTimeline,
  callingSubmissionToTimeline,
  marketerOtherToTimeline,
  marketerRowToTimeline,
  scriptsToTimelineRows,
  scriptwriterOtherToTimeline,
  scriptwriterSubmissionToTimeline,
  seoOtherToTimeline,
  seoTaskToTimeline,
  smmOtherToTimeline,
  smmTaskToTimeline,
} from "@/lib/timeline";

const VIEW_MODE_KEY = "taskViewMode";
function initialViewMode(): ViewMode {
  if (typeof window === "undefined") return "timeline";
  const v = window.localStorage.getItem(VIEW_MODE_KEY);
  return v === "list" || v === "timeline" ? v : "timeline";
}
function persistViewMode(v: ViewMode) {
  if (typeof window !== "undefined") window.localStorage.setItem(VIEW_MODE_KEY, v);
}

function TopFilterBar({
  range,
  onChange,
  total,
  filtered,
  viewMode,
  onViewModeChange,
}: {
  range: DateRange;
  onChange: (r: DateRange) => void;
  total: number;
  filtered: number;
  viewMode?: ViewMode;
  onViewModeChange?: (v: ViewMode) => void;
}) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-3 shadow-sm">
      <AdsDateRangePicker value={range} onChange={onChange} className="mb-0" />
      <div className="flex items-center gap-3">
        <span className="text-[11px] text-muted-foreground">
          Showing <strong className="text-foreground">{filtered}</strong> of {total}
        </span>
        {viewMode && onViewModeChange && (
          <ViewModeToggle value={viewMode} onChange={onViewModeChange} />
        )}
      </div>
    </div>
  );
}
import { Button } from "@/components/ui/button";
import { useMySubmissions } from "@/hooks/use-my-submissions";
import { useMyOtherTasks, useMyVideoSubmissions } from "@/hooks/use-editor-tasks";
import { useMyDesignerOtherTasks, useMyDesignerSubmissions } from "@/hooks/use-designer-tasks";
import { useMyAiOtherTasks, useMyAiSubmissions } from "@/hooks/use-ai-tasks";
import { useMyScriptwriterOtherTasks, useMyScriptwriterSubmissions } from "@/hooks/use-scriptwriter-tasks";
import { useMyMarketerOtherTasks, useMyMarketerScripts, useMyMarketerTasks } from "@/hooks/use-marketer-tasks";
import { useMySeoOtherTasks, useMySeoTasks } from "@/hooks/use-seo-tasks";
import { useAuth } from "@/lib/auth-context";
import { getDepartmentConfig } from "@/lib/department-configs";
import type { Submission } from "@/lib/submissions";
import type { EditorOtherTask, EditorVideoSubmission } from "@/lib/editor-tasks";
import type { DesignerOtherTask, DesignerSubmission } from "@/lib/designer-tasks";
import type { AiOtherTask, AiSubmission } from "@/lib/ai-tasks";
import type { ScriptwriterOtherTask, ScriptwriterSubmission } from "@/lib/scriptwriter-tasks";
import { scriptToMarketerRow, type MarketerOtherTask, type MarketerTask, type MarketerTaskRow } from "@/lib/marketer-tasks";
import type { SeoOtherTask, SeoTask } from "@/lib/seo-tasks";
import { exportSubmissionsCsv } from "@/lib/export-csv";

export const Route = createFileRoute("/_app/update-task")({
  component: UpdateTaskPage,
});

function UpdateTaskPage() {
  const { profile } = useAuth();
  if (!profile) return null;
  if (profile.department === "admin") return <AdminView />;
  if (profile.department === "editor") return <EditorView />;
  if (profile.department === "graphic_designer") return <DesignerView />;
  if (profile.department === "ai") return <AiView />;
  if (profile.department === "scriptwriter") return <ScriptwriterView />;
  if (profile.department === "marketer") return <MarketerView />;
  if (profile.department === "seo") return <SeoView />;
  if (profile.department === "smm") return <SmmView />;
  if (profile.department === "calling") return <CallingView />;
  return <GenericView />;
}

function CallingView() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"task" | "other">("task");
  const [viewMode, _setViewMode] = useState<ViewMode>(initialViewMode); const setViewMode = (v: ViewMode) => { _setViewMode(v); persistViewMode(v); };
  const [editingTask, setEditingTask] = useState<CallingSubmission | null>(null);
  const [editingOther, setEditingOther] = useState<CallingOtherTask | null>(null);
  const { data: tasks = [] } = useMyCallingSubmissions();
  const { data: others = [] } = useMyCallingOtherTasks();
  const [range, setRange] = useState(EMPTY_RANGE);
  const filtered_tasks = useMemo(() => filterByDateField(tasks, "submission_date", range), [tasks, range]);
  const filtered_others = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);
  const timeline_tasks = useMemo(() => filtered_tasks.map(callingSubmissionToTimeline), [filtered_tasks]);
  const timeline_others = useMemo(() => filtered_others.map(callingOtherToTimeline), [filtered_others]);

  return (
    <>
      <AppHeader subtitle="Log calling sessions and other tasks" ctaLabel="+ New Entry"
        onCta={() => { setEditingTask(null); setEditingOther(null); setOpen(true); }} />
      <div className="px-[22px] pb-6">
        <TopFilterBar
          range={range}
          onChange={setRange}
          total={tasks.length + others.length}
          filtered={filtered_tasks.length + filtered_others.length}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
        <Tabs value={tab} onValueChange={(v) => setTab(v as never)}>
          <TabsList>
            <TabsTrigger value="task">Calling Submissions</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="task" className="mt-4">
            {viewMode === "timeline" ? (
              <TimelineView rows={timeline_tasks} />
            ) : (
              <CallingSubmissionsTable rows={filtered_tasks} onEdit={(r) => { setEditingOther(null); setEditingTask(r); setOpen(true); }} />
            )}
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            {viewMode === "timeline" ? (
              <TimelineView rows={timeline_others} />
            ) : (
              <OtherTasksTable department="calling" rows={filtered_others} onEdit={(r) => { setEditingTask(null); setEditingOther(r); setOpen(true); }} />
            )}
          </TabsContent>
        </Tabs>
      </div>
      <CallingTaskUpdateModal open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) { setEditingTask(null); setEditingOther(null); } }}
        editingTask={editingTask} editingOther={editingOther} initialTab={tab} />
    </>
  );
}

function AdminView() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"task" | "other">("task");
  const [viewMode, _setViewMode] = useState<ViewMode>(initialViewMode); const setViewMode = (v: ViewMode) => { _setViewMode(v); persistViewMode(v); };
  const [editingTask, setEditingTask] = useState<AdminTask | null>(null);
  const [editingOther, setEditingOther] = useState<AdminOtherTask | null>(null);
  const { data: tasks = [] } = useMyAdminTasks();
  const { data: others = [] } = useMyAdminOtherTasks();
  const [range, setRange] = useState(EMPTY_RANGE);
  const filtered_tasks = useMemo(() => filterByDateField(tasks, "task_date", range), [tasks, range]);
  const filtered_others = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);
  const timeline_tasks = useMemo(() => filtered_tasks.map(adminTaskToTimeline), [filtered_tasks]);
  const timeline_others = useMemo(() => filtered_others.map(adminOtherToTimeline), [filtered_others]);

  return (
    <>
      <AppHeader subtitle="Log admin work and other tasks" ctaLabel="+ New Entry"
        onCta={() => { setEditingTask(null); setEditingOther(null); setOpen(true); }} />
      <div className="px-[22px] pb-6">
        <TopFilterBar
          range={range}
          onChange={setRange}
          total={tasks.length + others.length}
          filtered={filtered_tasks.length + filtered_others.length}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
        <Tabs value={tab} onValueChange={(v) => setTab(v as never)}>
          <TabsList>
            <TabsTrigger value="task">Admin Tasks</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="task" className="mt-4">
            {viewMode === "timeline" ? (
              <TimelineView rows={timeline_tasks} />
            ) : (
              <AdminTasksTable rows={filtered_tasks} onEdit={(r) => { setEditingOther(null); setEditingTask(r); setOpen(true); }} />
            )}
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            {viewMode === "timeline" ? (
              <TimelineView rows={timeline_others} />
            ) : (
              <OtherTasksTable department="admin" rows={filtered_others} onEdit={(r) => { setEditingTask(null); setEditingOther(r); setOpen(true); }} />
            )}
          </TabsContent>
        </Tabs>
      </div>
      <AdminTaskUpdateModal open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) { setEditingTask(null); setEditingOther(null); } }}
        editingTask={editingTask} editingOther={editingOther} initialTab={tab} />
    </>
  );
}

function SmmView() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"task" | "other">("task");
  const [viewMode, _setViewMode] = useState<ViewMode>(initialViewMode); const setViewMode = (v: ViewMode) => { _setViewMode(v); persistViewMode(v); };
  const [editingTask, setEditingTask] = useState<SmmTask | null>(null);
  const [editingOther, setEditingOther] = useState<SmmOtherTask | null>(null);
  const { data: tasks = [] } = useMySmmTasks();
  const { data: others = [] } = useMySmmOtherTasks();
  const [range, setRange] = useState(EMPTY_RANGE);
  const filtered_tasks = useMemo(() => filterByDateField(tasks, "task_date", range), [tasks, range]);
  const filtered_others = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);
  const timeline_tasks = useMemo(() => filtered_tasks.map(smmTaskToTimeline), [filtered_tasks]);
  const timeline_others = useMemo(() => filtered_others.map(smmOtherToTimeline), [filtered_others]);

  return (
    <>
      <AppHeader subtitle="Log SMM work, influencer funnel, and shoots" ctaLabel="+ New Entry"
        onCta={() => { setEditingTask(null); setEditingOther(null); setOpen(true); }} />
      <div className="px-[22px] pb-6">
        <TopFilterBar
          range={range}
          onChange={setRange}
          total={tasks.length + others.length}
          filtered={filtered_tasks.length + filtered_others.length}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
        <Tabs value={tab} onValueChange={(v) => setTab(v as never)}>
          <TabsList>
            <TabsTrigger value="task">SMM Tasks</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="task" className="mt-4">
            {viewMode === "timeline" ? (
              <TimelineView rows={timeline_tasks} />
            ) : (
              <SmmTasksTable rows={filtered_tasks} onEdit={(r) => { setEditingOther(null); setEditingTask(r); setOpen(true); }} />
            )}
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            {viewMode === "timeline" ? (
              <TimelineView rows={timeline_others} />
            ) : (
              <OtherTasksTable department="smm" rows={filtered_others} onEdit={(r) => { setEditingTask(null); setEditingOther(r); setOpen(true); }} />
            )}
          </TabsContent>
        </Tabs>
      </div>
      <SmmTaskUpdateModal open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) { setEditingTask(null); setEditingOther(null); } }}
        editingTask={editingTask} editingOther={editingOther} initialTab={tab} />
    </>
  );
}

function MarketerView() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"task" | "other">("task");
  const [viewMode, _setViewMode] = useState<ViewMode>(initialViewMode); const setViewMode = (v: ViewMode) => { _setViewMode(v); persistViewMode(v); };
  const [editingTask, setEditingTask] = useState<MarketerTask | null>(null);
  const [editingOther, setEditingOther] = useState<MarketerOtherTask | null>(null);
  const { data: tasks = [] } = useMyMarketerTasks();
  const { data: others = [] } = useMyMarketerOtherTasks();
  const { data: scripts = [] } = useMyMarketerScripts();
  const [range, setRange] = useState(EMPTY_RANGE);
  const merged_tasks = useMemo<MarketerTaskRow[]>(() => {
    const all: MarketerTaskRow[] = [
      ...tasks.map((t) => ({ ...t, _source: "task" as const })),
      ...scripts.map(scriptToMarketerRow),
    ];
    return all.sort((a, b) => {
      const d = (b.task_date ?? "").localeCompare(a.task_date ?? "");
      if (d !== 0) return d;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [tasks, scripts]);
  const filtered_tasks = useMemo(() => filterByDateField(merged_tasks, "task_date", range), [merged_tasks, range]);
  const filtered_others = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);
  const filtered_scripts = useMemo(() => filterByDateField(scripts, "submission_date", range), [scripts, range]);
  const timeline_tasks = useMemo(() => {
    const taskRows = filtered_tasks
      .filter((r) => r._source !== "script")
      .map(marketerRowToTimeline);
    const scriptRows = scriptsToTimelineRows(filtered_scripts);
    return [...taskRows, ...scriptRows].sort((a, b) => {
      const d = b.date.localeCompare(a.date);
      if (d !== 0) return d;
      return (b.startTime ?? "").localeCompare(a.startTime ?? "");
    });
  }, [filtered_tasks, filtered_scripts]);
  const timeline_others = useMemo(() => filtered_others.map(marketerOtherToTimeline), [filtered_others]);

  return (
    <>
      <AppHeader subtitle="Log marketing campaigns and other work" ctaLabel="+ New Entry"
        onCta={() => { setEditingTask(null); setEditingOther(null); setOpen(true); }} />
      <div className="px-[22px] pb-6">
        <TopFilterBar
          range={range}
          onChange={setRange}
          total={tasks.length + others.length}
          filtered={filtered_tasks.length + filtered_others.length}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
        <Tabs value={tab} onValueChange={(v) => setTab(v as never)}>
          <TabsList>
            <TabsTrigger value="task">Marketing Tasks</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="task" className="mt-4">
            {viewMode === "timeline" ? (
              <TimelineView rows={timeline_tasks} />
            ) : (
              <MarketerTasksTable rows={filtered_tasks} onEdit={(r) => { setEditingOther(null); setEditingTask(r); setOpen(true); }} />
            )}
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            {viewMode === "timeline" ? (
              <TimelineView rows={timeline_others} />
            ) : (
              <OtherTasksTable department="marketer" rows={filtered_others} onEdit={(r) => { setEditingTask(null); setEditingOther(r); setOpen(true); }} />
            )}
          </TabsContent>
        </Tabs>
      </div>
      <MarketerTaskUpdateModal open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) { setEditingTask(null); setEditingOther(null); } }}
        editingTask={editingTask} editingOther={editingOther} initialTab={tab} />
    </>
  );
}

function SeoView() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"task" | "other">("task");
  const [viewMode, _setViewMode] = useState<ViewMode>(initialViewMode); const setViewMode = (v: ViewMode) => { _setViewMode(v); persistViewMode(v); };
  const [editingTask, setEditingTask] = useState<SeoTask | null>(null);
  const [editingOther, setEditingOther] = useState<SeoOtherTask | null>(null);
  const { data: tasks = [] } = useMySeoTasks();
  const { data: others = [] } = useMySeoOtherTasks();
  const [range, setRange] = useState(EMPTY_RANGE);
  const filtered_tasks = useMemo(() => filterByDateField(tasks, "task_date", range), [tasks, range]);
  const filtered_others = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);
  const timeline_tasks = useMemo(() => filtered_tasks.map(seoTaskToTimeline), [filtered_tasks]);
  const timeline_others = useMemo(() => filtered_others.map(seoOtherToTimeline), [filtered_others]);

  return (
    <>
      <AppHeader subtitle="Log SEO work with time tracking" ctaLabel="+ New Entry"
        onCta={() => { setEditingTask(null); setEditingOther(null); setOpen(true); }} />
      <div className="px-[22px] pb-6">
        <TopFilterBar
          range={range}
          onChange={setRange}
          total={tasks.length + others.length}
          filtered={filtered_tasks.length + filtered_others.length}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
        <Tabs value={tab} onValueChange={(v) => setTab(v as never)}>
          <TabsList>
            <TabsTrigger value="task">SEO Tasks</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="task" className="mt-4">
            {viewMode === "timeline" ? (
              <TimelineView rows={timeline_tasks} />
            ) : (
              <SeoTasksTable rows={filtered_tasks} onEdit={(r) => { setEditingOther(null); setEditingTask(r); setOpen(true); }} />
            )}
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            {viewMode === "timeline" ? (
              <TimelineView rows={timeline_others} />
            ) : (
              <OtherTasksTable department="seo" rows={filtered_others} onEdit={(r) => { setEditingTask(null); setEditingOther(r); setOpen(true); }} />
            )}
          </TabsContent>
        </Tabs>
      </div>
      <SeoTaskUpdateModal open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) { setEditingTask(null); setEditingOther(null); } }}
        editingTask={editingTask} editingOther={editingOther} initialTab={tab} />
    </>
  );
}

function ScriptwriterView() {
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [tab, setTab] = useState<"script" | "other">("script");
  const [viewMode, _setViewMode] = useState<ViewMode>(initialViewMode); const setViewMode = (v: ViewMode) => { _setViewMode(v); persistViewMode(v); };
  const [editingScript, setEditingScript] = useState<ScriptwriterSubmission | null>(null);
  const [editingOther, setEditingOther] = useState<ScriptwriterOtherTask | null>(null);

  const { data: scripts = [] } = useMyScriptwriterSubmissions();
  const { data: others = [] } = useMyScriptwriterOtherTasks();
  const [range, setRange] = useState(EMPTY_RANGE);
  const filtered_scripts = useMemo(() => filterByDateField(scripts, "submission_date", range), [scripts, range]);
  const filtered_others = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);
  const timeline_scripts = useMemo(() => filtered_scripts.map(scriptwriterSubmissionToTimeline), [filtered_scripts]);
  const timeline_others = useMemo(() => filtered_others.map(scriptwriterOtherToTimeline), [filtered_others]);


  return (
    <>
      <AppHeader
        subtitle="Submit and manage your script work"
        ctaLabel="+ New Entry"
        onCta={() => { setEditingScript(null); setEditingOther(null); setOpen(true); }}
        extraActions={
          <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)}>Bulk Add</Button>
        }
      />
      <div className="px-[22px] pb-6">
        <TopFilterBar
          range={range}
          onChange={setRange}
          total={scripts.length + others.length}
          filtered={filtered_scripts.length + filtered_others.length}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
        <Tabs value={tab} onValueChange={(v) => setTab(v as never)}>
          <TabsList>
            <TabsTrigger value="script">Script Submissions</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="script" className="mt-4">
            {viewMode === "timeline" ? (
              <TimelineView rows={timeline_scripts} />
            ) : (
              <ScriptwriterSubmissionsTable rows={filtered_scripts} onEdit={(r) => { setEditingOther(null); setEditingScript(r); setOpen(true); }} />
            )}
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            {viewMode === "timeline" ? (
              <TimelineView rows={timeline_others} />
            ) : (
              <OtherTasksTable department="scriptwriter" rows={filtered_others} onEdit={(r) => { setEditingScript(null); setEditingOther(r); setOpen(true); }} />
            )}
          </TabsContent>
        </Tabs>
      </div>
      <ScriptwriterTaskUpdateModal
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) { setEditingScript(null); setEditingOther(null); } }}
        editingScript={editingScript}
        editingOther={editingOther}
        initialTab={tab}
      />
      <ScriptwriterBulkAddModal open={bulkOpen} onOpenChange={setBulkOpen} />
    </>
  );
}

function EditorView() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"video" | "other">("video");
  const [editingVideo, setEditingVideo] = useState<EditorVideoSubmission | null>(null);
  const [editingOther, setEditingOther] = useState<EditorOtherTask | null>(null);

  const { data: videos = [] } = useMyVideoSubmissions();
  const { data: others = [] } = useMyOtherTasks();
  const [range, setRange] = useState(EMPTY_RANGE);
  const filtered_videos = useMemo(() => filterByDateField(videos, "submission_date", range), [videos, range]);
  const filtered_others = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);


  return (
    <>
      <AppHeader
        subtitle="Submit and manage your daily work"
        ctaLabel="+ New Entry"
        onCta={() => { setEditingVideo(null); setEditingOther(null); setOpen(true); }}
      />
      <div className="px-[22px] pb-6">
        <DateRangeFilter
          value={range}
          onChange={setRange}
          totalCount={videos.length + others.length}
          filteredCount={filtered_videos.length + filtered_others.length}
        />
        <Tabs value={tab} onValueChange={(v) => setTab(v as never)}>
          <TabsList>
            <TabsTrigger value="video">Video Submissions</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="video" className="mt-4">
            <VideoSubmissionsTable rows={filtered_videos} onEdit={(r) => { setEditingOther(null); setEditingVideo(r); setOpen(true); }} />
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            <OtherTasksTable department="editor" rows={filtered_others} onEdit={(r) => { setEditingVideo(null); setEditingOther(r); setOpen(true); }} />
          </TabsContent>
        </Tabs>
      </div>
      <TaskUpdateModal
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) { setEditingVideo(null); setEditingOther(null); } }}
        editingVideo={editingVideo}
        editingOther={editingOther}
        initialTab={tab}
      />
    </>
  );
}

function DesignerView() {
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [tab, setTab] = useState<"creative" | "other">("creative");
  const [editingCreative, setEditingCreative] = useState<DesignerSubmission | null>(null);
  const [editingOther, setEditingOther] = useState<DesignerOtherTask | null>(null);

  const { data: creatives = [] } = useMyDesignerSubmissions();
  const { data: others = [] } = useMyDesignerOtherTasks();
  const [range, setRange] = useState(EMPTY_RANGE);
  const filtered_creatives = useMemo(() => filterByDateField(creatives, "submission_date", range), [creatives, range]);
  const filtered_others = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);


  return (
    <>
      <AppHeader
        subtitle="Submit and manage your design work"
        ctaLabel="+ New Entry"
        onCta={() => { setEditingCreative(null); setEditingOther(null); setOpen(true); }}
        extraActions={
          <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)}>
            Bulk Add
          </Button>
        }
      />
      <div className="px-[22px] pb-6">
        <DateRangeFilter
          value={range}
          onChange={setRange}
          totalCount={creatives.length + others.length}
          filteredCount={filtered_creatives.length + filtered_others.length}
        />
        <Tabs value={tab} onValueChange={(v) => setTab(v as never)}>
          <TabsList>
            <TabsTrigger value="creative">Creative Submissions</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="creative" className="mt-4">
            <DesignerSubmissionsTable rows={filtered_creatives} onEdit={(r) => { setEditingOther(null); setEditingCreative(r); setOpen(true); }} />
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            <OtherTasksTable department="graphic_designer" rows={filtered_others} onEdit={(r) => { setEditingCreative(null); setEditingOther(r); setOpen(true); }} />
          </TabsContent>
        </Tabs>
      </div>
      <DesignerTaskUpdateModal
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) { setEditingCreative(null); setEditingOther(null); } }}
        editingCreative={editingCreative}
        editingOther={editingOther}
        initialTab={tab}
      />
      <DesignerBulkAddModal open={bulkOpen} onOpenChange={setBulkOpen} />
    </>
  );
}

function AiView() {
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [tab, setTab] = useState<"creative" | "other">("creative");
  const [editingCreative, setEditingCreative] = useState<AiSubmission | null>(null);
  const [editingOther, setEditingOther] = useState<AiOtherTask | null>(null);

  const { data: creatives = [] } = useMyAiSubmissions();
  const { data: others = [] } = useMyAiOtherTasks();
  const [range, setRange] = useState(EMPTY_RANGE);
  const filtered_creatives = useMemo(() => filterByDateField(creatives, "submission_date", range), [creatives, range]);
  const filtered_others = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);


  return (
    <>
      <AppHeader
        subtitle="Submit and manage your AI work"
        ctaLabel="+ New Entry"
        onCta={() => { setEditingCreative(null); setEditingOther(null); setOpen(true); }}
        extraActions={
          <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)}>
            Bulk Add
          </Button>
        }
      />
      <div className="px-[22px] pb-6">
        <DateRangeFilter
          value={range}
          onChange={setRange}
          totalCount={creatives.length + others.length}
          filteredCount={filtered_creatives.length + filtered_others.length}
        />
        <Tabs value={tab} onValueChange={(v) => setTab(v as never)}>
          <TabsList>
            <TabsTrigger value="creative">Creative Submissions</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="creative" className="mt-4">
            <AiSubmissionsTable rows={filtered_creatives} onEdit={(r) => { setEditingOther(null); setEditingCreative(r); setOpen(true); }} />
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            <OtherTasksTable department="ai" rows={filtered_others} onEdit={(r) => { setEditingCreative(null); setEditingOther(r); setOpen(true); }} />
          </TabsContent>
        </Tabs>
      </div>
      <AiTaskUpdateModal
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) { setEditingCreative(null); setEditingOther(null); } }}
        editingCreative={editingCreative}
        editingOther={editingOther}
        initialTab={tab}
      />
      <AiBulkAddModal open={bulkOpen} onOpenChange={setBulkOpen} />
    </>
  );
}

function GenericView() {
  const { profile } = useAuth();
  const { data: rows = [] } = useMySubmissions();
  const [range, setRange] = useState(EMPTY_RANGE);
  const filteredRows = useMemo(() => filterByDateField(rows, "submission_date", range), [rows, range]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Submission | null>(null);

  if (!profile) return null;
  const config = getDepartmentConfig(profile.department);

  return (
    <>
      <AppHeader
        subtitle={`Log and manage your daily ${config.itemNoun}s`}
        ctaLabel={config.ctaLabel}
        onCta={() => { setEditing(null); setOpen(true); }}
        onExport={() => exportSubmissionsCsv(filteredRows, `${profile.department}-tasks.csv`, { includePoints: profile.department === "editor" })}
      />
      <div className="px-[22px] pb-6">
        <SubmissionsTable rows={rows} config={config} onEdit={(s) => { setEditing(s); setOpen(true); }} />
      </div>
      <SubmissionModal open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }} config={config} editing={editing} />
    </>
  );
}
